import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { CascadeSelectModule } from 'primeng/cascadeselect';
import { ChipModule } from 'primeng/chip';
import { InputTextModule } from "primeng/inputtext";
import { Textarea } from 'primeng/textarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { SpeedDialModule } from 'primeng/speeddial';
import { TreeSelectModule } from 'primeng/treeselect';
import { DialogService } from '../../../../../@core/services/dialog.service';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { ChecklistsService } from "../../../../services/checklists.service";
import { IChecklistItem, IChecklistItemTag, IDocumentType, ITag, ITagType, mappingType } from "../../../../models/settings.model";
import { TagsService } from "../../../../services/tags.service";
import { CardModule } from "primeng/card";
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SkeletonModule } from "primeng/skeleton";
import { SkeletonService } from "../../../../services/skeleton.service";
import _ from "lodash";
import { ToastService } from "../../../../../@core/services/toast.service";
import { DialogComponent } from '../../../../../@core/components/ui/dialog/dialog.component';
import { TabsModule } from "primeng/tabs";
import { CheckboxModule } from "primeng/checkbox";
import { CommonModule, TitleCasePipe } from "@angular/common";
import { ListboxModule } from 'primeng/listbox';
import { TAG_DATA_TYPES } from "../../../../../@core/models/enums";
import { DocumentTypeService } from "../../../../services/document-type.service";
import { Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import { ButtonComponent } from "../../../../../@core/components/ui/button/button.component";
import { CheckboxComponent } from "../../../../../@core/components/ui/checkbox/checkbox.component";
import { DeleteConfirmationComponent } from "../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component";

@Component({
    selector: 'app-checklists',
    imports: [
        SpeedDialModule,
        InputNumberModule,
        InputTextModule,
        Textarea,
        ButtonModule,
        CascadeSelectModule,
        FormsModule,
        ChipModule,
        TreeSelectModule,
        AccordionModule,
        CardModule,
        PaginatorModule,
        SkeletonModule,
        DialogComponent,
        TabsModule,
        CheckboxModule,
        ReactiveFormsModule,
        CommonModule,
        ListboxModule,
        TooltipModule,
        TitleCasePipe,
        ButtonComponent,
        CheckboxComponent,
        DeleteConfirmationComponent
    ],
    standalone: true,
    templateUrl: './checklists.component.html',
    styleUrl: './checklists.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChecklistsComponent implements OnInit, OnDestroy {

    checklists: Partial<IChecklistItem & { checkPoints: ITagType[] }>[] = []

    tagTypes: ITagType[] = [];
    tagTypesById: { [key: string]: ITagType } = {};

    tagsByTagType: { [key: string]: ITag[] } = {};
    tagsById: { [key: string]: ITag } = {};
    selectedChecklist!: string;
    selectedTagsByChecklist: {
        [key: string]: {
            all: boolean,
            mappingAll: boolean,
            tagTypes: {
                [key: string]: {
                    all: boolean,
                    values: string[]
                }
            },
        }
    } = {}
    changesDone: boolean = false;

    checklistsTagsMap: { [key: string]: IChecklistItemTag } = {} // key -> checklist#tagType#value

    FIELDS: { [K in keyof Partial<IChecklistItem>]: keyof Partial<IChecklistItem> } = {
        name: 'name',
        description: 'description',
    }

    loading: boolean = true;
    tagLoading: boolean = false;
    chipLoading: boolean = false;
    tagTypesMap: { [key: string]: string } = {}
    skeletonCount: number = 1;
    openAddTagDialog: boolean = false;
    emptyTitleWarnings: { [checklistId: string]: boolean } = {};
    emptyDescriptionWarnings: { [checklistId: string]: boolean } = {};


    //paginator
    defaultPaginatorValues = {
        first: 0,
        rows: 20
    }
    first: number = this.defaultPaginatorValues.first;
    rows: number = this.defaultPaginatorValues.rows;
    totalRecords: number = 0;

    isLoadingTags = false;
    tagTypeIdx = 0

    docTypeLoading: boolean = false
    documentTypes: IDocumentType[] = [];
    selectedDocType!: string;
    checklistSavingInProgress = false;
    addChecklistLoader: boolean = false
    addChecklistVisible = false
    checklistForm: FormGroup = new FormGroup({
        name: new FormControl('', [Validators.required]),
        definition: new FormControl('', [Validators.required]),
    })
    private unsubscribeChecklists$ = new Subject<void>();

    deleteChecklistDialogVisible = false;
    checklistIdToDelete: string | null = null;


    constructor(
        private cdr: ChangeDetectorRef,
        private checklistsService: ChecklistsService,
        private tagsService: TagsService,
        private skeletonService: SkeletonService,
        private dialogService: DialogService,
        private toastService: ToastService,
        private documentTypeService: DocumentTypeService,
        private router: Router
    ) { }

    async ngOnInit() {
        this.setupResizeObserver();
        this.getDocumentTypes()
    }

    get checklistFormControls() {
        return this.checklistForm.controls;
    }

    ngOnDestroy() {
        this.cleanupResizeObserver();
    }

    //#region skeleton operations
    private setupResizeObserver() {
        this.skeletonService.setupResizeObserver(() => {
            this.calculateSkeletonCount();
            this.cdr.detectChanges();
        });
    }

    private cleanupResizeObserver() {
        this.skeletonService.cleanupResizeObserver();
    }

    calculateSkeletonCount() {
        const itemHeight = 5 * 16; // 15rem converted to pixels (assuming 1rem = 16px)
        const gap = 4 * 16; // 4rem gap converted to pixels (matching the gap-4 class)
        const padding = 4 * 16; // 4rem padding (1rem top + 1rem bottom)

        this.skeletonCount = this.skeletonService.calculateSkeletonCount(itemHeight, gap, padding);
    }

    getSkeletonCount(): number[] {
        return this.skeletonService.getSkeletonArray(this.skeletonCount);
    }
    //#endregion

    //#region crud operations

    getTagTypes() {
        return new Promise((resolve, reject) => {
            this.tagsService.getTagTypes({ filters: { dataType: [TAG_DATA_TYPES.DROPDOWN], documentType: this.selectedDocType }, getLinkedTags: true }).subscribe((res) => {
                if (res && res.success) {
                    const result = res.data;
                    this.tagTypes = result?.tagTypes || []
                    this.tagTypesById = _.keyBy(this.tagTypes, '_id');

                    this.tagsByTagType = result?.linkedTags || {};
                    this.tagsById = _.keyBy(Object.values(this.tagsByTagType).flat(), '_id');
                    resolve(true)
                } else {
                    this.tagTypes = [];
                    this.tagTypesById = {};
                    this.tagsByTagType = {};
                    this.tagsById = {};
                    reject(false)
                }
            })
        })
    }

    getDocumentTypes() {
        this.docTypeLoading = true
        this.documentTypeService.getDocTypes().subscribe(res => {
            if (res && res.success) {
                this.docTypeLoading = false
                this.documentTypes = res.data || [];
                if (this.documentTypes.length > 0) {
                    this.changeDocumentType(this.selectedDocType ?? this.documentTypes[0]._id);
                }
            }
            else
                this.docTypeLoading = false
        })
    }

    async changeDocumentType(documentType: string) {
        this.loading = true
        this.selectedDocType = documentType;
        this.unsubscribeChecklists$.next();
        await this.getTagTypes()
        this.onPageChange({});
    }

    onPageChange(event: PaginatorState) {
        this.first = event.first ?? this.defaultPaginatorValues.first;
        this.rows = event.rows ?? this.defaultPaginatorValues.rows;
        const skip = this.first;
        this.getChecklists(skip, this.rows)
    }

    getChecklists(skip = this.defaultPaginatorValues.first, limit = this.defaultPaginatorValues.rows) {
        // Clear existing checklists and selectedTags
        this.checklists = [];
        this.selectedTagsByChecklist = {};
        this.checklistsService.getChecklists({ fetchTags: true, skip: skip, limit: limit, documentType: this.selectedDocType }).pipe(
            takeUntil(this.unsubscribeChecklists$)
        ).subscribe(res => {
            if (res && res.success) {
                const checklists = res.data?.checklists || [];
                const tagsData: { [key: string]: IChecklistItemTag[] } = res.data?.tags || {};

                // Clear existing checklists and selectedTags
                this.checklists = [];
                this.selectedTagsByChecklist = {};
                for (let i = 0; i < checklists.length; i++) {
                    const checklist = checklists[i];
                    this.checklists.push(checklist)
                    if (checklist?._id) {
                        const key = checklist._id.toString();
                        const tagsOfCurrentChecklist: IChecklistItemTag[] = (tagsData[key] || []).filter(tag => this.tagTypesById[((tag.tagType as ITagType)?._id ?? '').toString()]);
                        let tags: { [key: string]: { all: boolean, values: string[] } } = {};

                        Object.keys(this.tagTypesById).forEach(tagTypeId => {
                            tags[tagTypeId] = { all: false, values: [] }
                        })
                        for (let tagMap of tagsOfCurrentChecklist) {

                            const tagType = (tagMap.tagType as ITagType)?._id;
                            const tag: ITag = (tagMap as IChecklistItemTag).value as ITag;

                            if (tagType && tag) {
                                const hashKey = `${tagMap.checklist}#${tagType}#${tag._id}`;
                                this.checklistsTagsMap[hashKey] = tagMap;

                                if (!tags[tagType]) {
                                    tags[tagType] = { all: false, values: [] };
                                }
                                tags[tagType].values.push(tag._id);

                                //in case any active is false
                                if (!this.tagsById[tag._id]) {
                                    this.tagsById[tag._id] = tag;
                                }
                            }

                        }
                        this.selectedTagsByChecklist[checklist._id] = {
                            all: checklist.mapping == mappingType.ALL,
                            mappingAll: checklist.mapping == mappingType.ALL,
                            tagTypes: tags
                        }
                        this.updateAllFieldsForChecklist(checklist._id)
                    }
                }
                // Update total records for paginator
                this.totalRecords = res.data?.total || 0;
                this.loading = false;
                this.cdr.detectChanges();
            } else {
                // Clear existing checklists and selectedTags
                this.checklists = [];
                this.selectedTagsByChecklist = {};
                this.totalRecords = 0;
                this.loading = false;
                this.cdr.detectChanges();
            }
        })
    }

    handleClickAddChecklist() {
        this.checklistForm.reset()
        this.addChecklistVisible = true
    }

    addNewChecklist() {
        this.addChecklistLoader = true
        if (this.checklistForm.invalid) {
            this.addChecklistLoader = false;
            this.toastService.warn('Please fill all the required fields');
            return
        }
        if (!this.checklists) {
            this.checklists = []
        }
        const newCheckList: Partial<IChecklistItem> = {
            name: this.checklistFormControls["name"].value as string,
            documentType: this.selectedDocType,
            description: this.checklistFormControls["definition"].value as string ?? '',
        }
        this.checklistSavingInProgress = true
        this.checklistsService.createChecklist(newCheckList).subscribe(res => {
            if (res && res.success) {
                this.checklistSavingInProgress = false;
                this.toastService.success("Checklist created successfully")
                this.onPageChange({
                    first: (Math.floor(this.totalRecords / this.rows) * (this.rows)),
                    rows: this.rows
                })
                this.addChecklistLoader = false
                this.addChecklistVisible = false
                this.cdr.detectChanges()
            }
            else {
                this.addChecklistLoader = false
                this.checklistSavingInProgress = false;
                this.cdr.detectChanges()
            }
        })
    }

    updateChecklistById(data: any, originalName: string | undefined, event: any) {
        this.checklistsService.updateChecklistById(data).subscribe(res => {
            if (res && res.success) {
                const index = this.checklists.findIndex(curr => curr._id === data._id);
                this.checklists[index] = res.data;
                this.cdr.detectChanges();
            }
            else {
                if (originalName) {
                    event.target.value = originalName
                }
            }
        })
    }

    copyChecklist(checklistId: string | undefined) {
        this.checklistsService.copyChecklist(checklistId as string).subscribe(res => {
            if (res.success) {
                this.toastService.success("Checklist copied successfully")
                this.onPageChange({
                    first: (Math.floor(this.totalRecords / this.rows) * (this.rows)),
                    rows: this.rows
                })
            }
        })
    }

    deleteChecklist(id: string | undefined) {
        if (id) {
            this.checklistIdToDelete = id;
            this.deleteChecklistDialogVisible = true;
        }
    }

    confirmDeleteChecklist() {
        if (this.checklistIdToDelete) {
            this.checklistsService.deleteChecklistById(this.checklistIdToDelete).subscribe((res) => {
                if (res.success) {
                    this.toastService.success('Checklist deleted successfully');
                    // If the last record is deleted, move to the previous page
                    if (((this.totalRecords - 1) % this.rows == 0) && this.first != 0) {
                        this.first = this.first - this.rows;
                    }
                    this.onPageChange({
                        first: this.first,
                        rows: this.rows
                    });
                    this.deleteChecklistDialogVisible = false;
                } else {
                    this.toastService.error('Error deleting checklist');
                    this.deleteChecklistDialogVisible = false;
                }
            });
        }
    }
    //#endregion

    update(checklistId: string | undefined, field: any, event: any) {
        const originalName = field == this.FIELDS.name
            ? this.checklists.find(item => item?._id === checklistId)?.name
            : undefined;

        if (!checklistId) return;

        let dataToUpdate: any = {};
        const newValue = event.target.value;
        dataToUpdate[field] = newValue;

        if (field === this.FIELDS.name) {
            if (newValue.trim().length !== 0) {
                this.emptyTitleWarnings[checklistId] = false;
                this.updateChecklistById({
                    _id: checklistId,
                    dataToUpdate,
                    docTypeId: this.selectedDocType
                }, originalName, event);
            } else {
                this.emptyTitleWarnings[checklistId] = true;
                this.cdr.detectChanges();
            }
        }

        if (field === this.FIELDS.description) {
            if (newValue.trim().length !== 0) {
                this.emptyDescriptionWarnings[checklistId] = false;
                this.updateChecklistById({
                    _id: checklistId,
                    dataToUpdate,
                    docTypeId: this.selectedDocType
                }, undefined, event);
            } else {
                this.emptyDescriptionWarnings[checklistId] = true;
                this.cdr.detectChanges();
            }
        }
    }



    removeChips(checklistId: string, tagType: string) {
        if (checklistId) {
            this.dialogService.confirm({
                title: 'Confirmation',
                description: 'This checklist may have contracts linked to it based on these tags. Would you like to delete any such mappings if they exist?',
                confirmText: 'Yes, delete mappings',
                cancelText: 'No, keep mappings',
            }).subscribe(confirmed => {
                const tags = this.selectedTagsByChecklist[checklistId].tagTypes[tagType].values;
                const tagsToDelete = tags.map(tag => {
                    const hashKey = `${checklistId}#${tagType}#${tag}`;
                    return this.checklistsTagsMap[hashKey]?._id;
                });
                this.deleteTagLinkedToChecklist(checklistId, tagsToDelete, confirmed);
            });
        }
    }

    removeChip(checklistId: string, tagType: string, tag: string) {
        const hashKey = `${checklistId}#${tagType}#${tag}`;
        const tagMap = this.checklistsTagsMap[hashKey];
        if (checklistId) {
            this.dialogService.confirm({
                title: 'Confirmation',
                description: 'This checklist may have contracts linked to it based on this tag. Would you like to delete any such mappings if they exist?',
                confirmText: 'Yes, delete mappings',
                cancelText: 'No, keep mappings',
            }).subscribe(confirmed => {
                this.deleteTagLinkedToChecklist(checklistId, [tagMap._id], confirmed);
            });
        }
    }

    deleteTagLinkedToChecklist(checklistId: string, tagsMap: IChecklistItemTag['_id'][], deleteMappings: boolean) {
        this.checklistsService.deleteTagLinkedToChecklist(checklistId, tagsMap, deleteMappings).subscribe(res => {
            if (res && res.success) {
                if (res.data)
                    this.updateTagsOnSaveDelete(res.data)
                this.toastService.success("Tag unlinked successfully")
            }
        })
    }

    toggleAddTagDialog() {
        this.openAddTagDialog = !this.openAddTagDialog;
        this.isLoadingTags = false;
    }

    onTabChange(event: any) {
        const index = parseInt(event.value || event.index || '0');
        this.tagTypeIdx = index;
        let tagTypeId = this.tagTypes[index]._id as string;
        if (!this.selectedTagsByChecklist[this.selectedChecklist].tagTypes[tagTypeId]) {
            this.selectedTagsByChecklist[this.selectedChecklist].tagTypes[tagTypeId] = {
                all: this.selectedTagsByChecklist[this.selectedChecklist].all ? true : false,
                values: this.selectedTagsByChecklist[this.selectedChecklist].all ? this.tagsByTagType[tagTypeId].map(tag => tag._id) : []
            }
        }
    }


    onClickAddTag(checklistId: string) {
        this.isLoadingTags = true;
        this.changesDone = false;
        this.selectedChecklist = checklistId;
        if (!this.selectedTagsByChecklist[checklistId] || !this.selectedTagsByChecklist[checklistId]?.tagTypes || Object.keys(this.selectedTagsByChecklist[checklistId].tagTypes).length === 0) {
            let tagTypes: { [key: string]: { all: boolean, values: string[] } } = {};
            Object.keys(this.tagTypesById).forEach(tagTypeId => {
                tagTypes[tagTypeId] = { all: false, values: [] }
            })
            const checklist = this.checklists.find(item => item._id == checklistId)
            this.selectedTagsByChecklist[checklistId] = { all: checklist?.mapping == mappingType.ALL, mappingAll: checklist?.mapping == mappingType.ALL, tagTypes: tagTypes }
        }
        this.toggleAddTagDialog();
    }

    onTagChange(event: any, tagType: string) {
        this.selectedTagsByChecklist[this.selectedChecklist].tagTypes[tagType].values = event;
        this.updateAllFieldsForTagType(this.selectedChecklist, tagType)
        this.cdr.markForCheck();
    }

    saveTags() {
        if (!this.changesDone) {
            this.tagTypeIdx = 0;
            return
        }
        const payload = Object.keys(this.selectedTagsByChecklist[this.selectedChecklist].tagTypes).flatMap(tagType => this.selectedTagsByChecklist[this.selectedChecklist].tagTypes[tagType].values.map(tag => ({
            checklist: this.selectedChecklist,
            tagType: tagType,
            tag: tag
        })))
        this.checklistsService.linkTagToChecklist(this.selectedChecklist, payload, this.selectedTagsByChecklist[this.selectedChecklist].all).subscribe(res => {
            if (res && res.success) {
                this.tagTypeIdx = 0;
                if (res.data)
                    this.updateTagsOnSaveDelete(res.data)
                this.toastService.success("Tags saved successfully")
                this.changesDone = false;
            } else {
                this.tagTypeIdx = 0;
                this.changesDone = false;
            }
        })
    }

    updateTagsOnSaveDelete(data: IChecklistItemTag[]) {
        const tagsOfCurrentChecklist: IChecklistItemTag[] = (data || []).filter(tag => this.tagTypesById[((tag.tagType as ITagType)?._id ?? '').toString()]);
        let tags: { [key: string]: { all: boolean, values: string[] } } = {};
        Object.keys(this.tagTypesById).forEach(tagTypeId => {
            tags[tagTypeId] = { all: false, values: [] }
        })
        for (let tagMap of tagsOfCurrentChecklist) {
            const tagType = (tagMap.tagType as ITagType)?._id;
            const tag: ITag = (tagMap as IChecklistItemTag).value as ITag;

            if (tagType && tag) {
                const hashKey = `${tagMap.checklist}#${tagType}#${tag._id}`;
                this.checklistsTagsMap[hashKey] = tagMap;

                if (!tags[tagType]) {
                    tags[tagType] = { all: false, values: [] };
                }
                tags[tagType].values.push(tag._id);

                //in case any active is false
                if (!this.tagsById[tag._id]) {
                    this.tagsById[tag._id] = tag;
                }
            }
        }
        const checklist = this.checklists.find(item => item._id == this.selectedChecklist)
        this.selectedTagsByChecklist[this.selectedChecklist] = {
            all: false,
            mappingAll: checklist?.mapping == mappingType.ALL,
            tagTypes: tags
        }
        this.updateAllFieldsForChecklist(this.selectedChecklist)
    }

    onSelectAllChange(event: any) {
        this.selectedTagsByChecklist[this.selectedChecklist].all = event.checked;
        Object.keys(this.selectedTagsByChecklist[this.selectedChecklist].tagTypes).forEach(tagType => {
            this.selectedTagsByChecklist[this.selectedChecklist].tagTypes[tagType].all = event.checked;
            this.selectedTagsByChecklist[this.selectedChecklist].tagTypes[tagType].values = event.checked ? this.tagsByTagType[tagType].map(tag => tag._id) : []
        })
        this.cdr.markForCheck();
    }

    updateAllFieldsForChecklist(checklistId: string) {
        for (let tagType in this.tagTypesById) {
            if (this.selectedTagsByChecklist?.[checklistId]?.tagTypes?.[tagType]) {
                this.updateAllFieldsForTagType(checklistId, tagType)
            }
        }
    }

    updateAllFieldsForTagType(checklistId: string, tagType: string) {
        this.selectedTagsByChecklist[checklistId].tagTypes[tagType].all = (this.tagsByTagType[tagType] || []).length > 0 ? this.tagsByTagType[tagType].every(tag => this.selectedTagsByChecklist[checklistId].tagTypes[tagType].values.includes(tag._id)) : this.selectedTagsByChecklist[checklistId].mappingAll
        this.updateAllForChecklist(checklistId)
    }

    updateAllForChecklist(checklistId: string) {
        let selectAllValue = true;
        Object.entries(this.selectedTagsByChecklist[checklistId].tagTypes).map(([key, value]) => {
            selectAllValue = selectAllValue && value.all;
        })
        this.selectedTagsByChecklist[checklistId].all = selectAllValue
    }

    addTagTypes() {
        this.router.navigate(['settings/tags'])
        return
    }
}
